/**
 * Payment terms page logic (DEC-326 item 7) — pure, so it is unit-tested apart from React.
 *
 * Terms are whole days, 0–365 (Net 30 = 30; 0 = due on receipt). A customer field left
 * BLANK means "use the company default" and is sent as null.
 */

export const MAX_TERMS_DAYS = 365;

export interface PaymentTermsCustomer {
  readonly haulerOperatorId: string;
  readonly name: string;
  readonly paymentTermsDays: number | null;
}

export interface PaymentTermsResponse {
  readonly defaultPaymentTermsDays: number;
  readonly customers: readonly PaymentTermsCustomer[];
}

export interface PaymentTermsBody {
  readonly defaultPaymentTermsDays: number;
  readonly customers: ReadonlyArray<{
    readonly haulerOperatorId: string;
    readonly paymentTermsDays: number | null;
  }>;
}

/** Parse a days field. '' → null (blank); a whole number 0–365 → it; anything else → undefined. */
export function parseTermsDays(raw: string): number | null | undefined {
  const s = raw.trim();
  if (s === '') return null;
  if (!/^\d{1,3}$/.test(s)) return undefined;
  const n = Number(s);
  return n <= MAX_TERMS_DAYS ? n : undefined;
}

export interface Draft {
  readonly defaultDays: string;
  /** haulerOperatorId → field text ('' = company default). */
  readonly overrides: Readonly<Record<string, string>>;
}

export function draftFrom(data: PaymentTermsResponse): Draft {
  const overrides: Record<string, string> = {};
  for (const c of data.customers) {
    overrides[c.haulerOperatorId] = c.paymentTermsDays === null ? '' : String(c.paymentTermsDays);
  }
  return { defaultDays: String(data.defaultPaymentTermsDays), overrides };
}

export type BuildResult =
  | { readonly kind: 'invalid'; readonly fields: readonly string[] }
  | { readonly kind: 'unchanged' }
  | { readonly kind: 'ok'; readonly body: PaymentTermsBody };

/**
 * The PUT body for a draft, carrying ONLY the customers whose value changed (the server
 * audits what it is sent). `fields` names what is invalid: 'default' or a customer id.
 */
export function buildSaveBody(saved: PaymentTermsResponse, draft: Draft): BuildResult {
  const invalid: string[] = [];
  const def = parseTermsDays(draft.defaultDays);
  // The company default cannot be blank.
  if (def === undefined || def === null) invalid.push('default');

  const customers: Array<{ haulerOperatorId: string; paymentTermsDays: number | null }> = [];
  for (const c of saved.customers) {
    const v = parseTermsDays(draft.overrides[c.haulerOperatorId] ?? '');
    if (v === undefined) {
      invalid.push(c.haulerOperatorId);
      continue;
    }
    if (v !== c.paymentTermsDays) {
      customers.push({ haulerOperatorId: c.haulerOperatorId, paymentTermsDays: v });
    }
  }

  if (invalid.length > 0 || def === undefined || def === null) {
    return { kind: 'invalid', fields: invalid };
  }
  if (def === saved.defaultPaymentTermsDays && customers.length === 0) return { kind: 'unchanged' };
  return { kind: 'ok', body: { defaultPaymentTermsDays: def, customers } };
}
